import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import SecondaryButton from "../../../ui/SecondaryButton";
import { useSearch } from "../../../contexts/PlayerSearchContext";
import { useClubSearch } from "../../../contexts/ClubSearchContext";
import { usePlayerSearch } from "../../../contexts/SearchPlayersContext";

const StyledSortContainer = styled.div`
  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.scrollbarTrack};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.scrollbarThumb};
  }
  & h3 {
    font-family: "DM Mono", monospace;
    font-weight: 500;
    font-size: 14px;
    color: ${({ theme }) => theme.NumberColor};
    margin-bottom: 8px;
  }
  & p {
    font-family: "Aeonik";
    font-size: 16px;
    color: ${({ theme }) => theme.playerDataValueColor};
    margin: 0;
    padding: 10px 5px;
  }
`;

const StyledHR = styled.hr`
  height: 2px;
  border: none;
  background-color: ${({ theme }) => theme.filterdropdownBorder};
`;

const StyledItemContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  background-color: ${({ isSelected, theme }) => (isSelected ? theme.dropdownBorderColor : "transparent")};
  padding: 5px 0;
`;

const SortIconContainer = styled.div`
  position: absolute;
  right: 10px;
`;

interface FilterSortProps {
  onSortChange?: (filter: string, order: string) => void;
  data?: any;
  sortType?: string;
}

const FilterSort: React.FC<FilterSortProps> = ({ onSortChange, data, sortType }) => {
  const { setSortOrdering, sortOrdering, sortOrderName, setSortOrderName, isFilterChange, setIsFilterChange } = useSearch();

  const {
    setSortSquadOrdering,
    sortSquadOrdering,
    sortSquadOrderName,
    setSortSquadOrderName,
    setIsSquadFilterChange,
    setSortClubCandidateOrderName,
    sortClubCandidateOrderName,
    setSortClubCandidateOrdering,
    sortClubCandidateOrdering,
    setIsClubCandidateSortFilterChange,
    isClubCandidateSortFilterChange,
    setSortClubOrdering,
    sortClubOrdering,
    sortClubOrderName,
    setSortClubOrderName,
    setIsClubSortFilterChange,
    setIsClubFilterChange
  } = useClubSearch();

  const { setResultSortOrderName, resultSortOrderName, setResultSortOrdering, setIsPlayerFilterChange, resultSortOrdering } =
    usePlayerSearch();

  const [sortState, setSortState] = useState<{ filter: string; order: "ASC" | "DESC" }>({
    filter: "Performance value",
    order: "ASC",
  });

  const [sortPlayerState, setSortPlayerState] = useState<{ filter: string; order: "ASC" | "DESC" }>({
    filter: "Similarity",
    order: "DESC",
  });

  const [sortPositionState, setSortPositionState] = useState<{ filter: string; order: "ASC" | "DESC" }>({
    filter: "Position",
    order: "ASC",
  });

  const [sortClubState, setSortClubState] = useState<{ filter: string; order: "ASC" | "DESC" }>({
    filter: "Total revenue",
    order: "ASC",
  });

  const [sortClubCandidateState, setSortClubCandidateState] = useState<{ filter: string; order: "ASC" | "DESC" }>({
    filter: "System Fit",
    order: "DESC",
  });

  const filterState = useMemo(
    () => ({
      NewPlayer: sortPlayerState,
      Player: sortState,
      Squad: sortPositionState,
      Club: sortClubState,
      clubCandidate: sortClubCandidateState,
    }),
    [sortPlayerState, sortState, sortPositionState, sortClubState, sortClubCandidateState, resultSortOrderName, resultSortOrdering]
  );

  useEffect(() => {
    initializeSortState();
  }, [
    sortOrdering,
    sortOrderName,
    sortSquadOrdering,
    sortSquadOrderName,
    sortClubOrdering,
    sortSquadOrderName,
    sortClubCandidateOrderName,
    sortClubCandidateOrdering,
    resultSortOrderName,
    resultSortOrdering,
    sortClubOrderName
  ]);

  const initializeSortState = () => {
    switch (sortType) {
      case "NewPlayer":
        setSortPlayerState({
          filter: resultSortOrderName || "Similarity",
          order: resultSortOrdering === "ASC" || resultSortOrdering === "DESC" ? resultSortOrdering : "DESC",
        });
        break;
      case "Player":
        setSortState({
          filter: sortOrderName || "Performance value",
          order: sortOrdering === "ASC" || sortOrdering === "DESC" ? sortOrdering : "ASC",
        });
        break;
      case "Squad":
        setSortPositionState({
          filter: sortSquadOrderName || "Position",
          order: sortSquadOrdering === "ASC" || sortSquadOrdering === "DESC" ? sortSquadOrdering : "ASC",
        });
        break;
      case "Club":
        setSortClubState({
          filter: sortClubOrderName || "Total revenue",
          order: sortClubOrdering === "ASC" || sortClubOrdering === "DESC" ? sortClubOrdering : "ASC",
        });
        break;
      case "clubCandidate":
        setSortClubCandidateState({
          filter: sortClubCandidateOrderName || "System Fit",
          order: sortClubCandidateOrdering === "ASC" || sortClubCandidateOrdering === "DESC" ? sortClubCandidateOrdering : "ASC",
        });
        break;
      default:
        break;
    }
  };

  const handleSortChange = (filter: string) => {
    switch (sortType) {
      case "NewPlayer":
        if (sortPlayerState.filter !== filter) {
          setSortPlayerState({ filter, order: "DESC" });
          setResultSortOrderName(filter);
          setResultSortOrdering("DESC");
          setIsPlayerFilterChange(true);
          onSortChange?.(filter, "DESC");
        }
        break;
      case "Player":
        if (sortState.filter !== filter) {
          setSortState({ filter, order: "ASC" });
          setSortOrderName(filter);
          setSortOrdering("ASC");
          setIsFilterChange(true);
          onSortChange?.(filter, "ASC");
        }
        break;
      case "Squad":
        if (sortPositionState.filter !== filter) {
          setSortPositionState({ filter, order: "ASC" });
          setSortSquadOrderName(filter);
          setSortSquadOrdering("ASC");
          setIsSquadFilterChange(true);
          onSortChange?.(filter, "ASC");
        }
        break;
      case "Club":
        if (sortClubState.filter !== filter) {
          setSortClubState({ filter, order: "ASC" });
          setSortClubOrderName(filter);
          setSortClubOrdering("ASC");
          setIsClubSortFilterChange(true);
          setIsClubFilterChange(true);
          onSortChange?.(filter, "ASC");
        }
        break;
      case "clubCandidate":
        if (sortClubCandidateState.filter !== filter) {
          setSortClubCandidateState({ filter, order: "ASC" });
          setSortClubCandidateOrderName(filter);
          setSortClubCandidateOrdering("ASC");
          setIsClubCandidateSortFilterChange(true);
          onSortChange?.(filter, "ASC");
        }
        break;
      default:
        break;
    }
  };

  const toggleSortOrder = useCallback(() => {
    const state = filterState[sortType as keyof typeof filterState];
    const newOrder = state.order === "ASC" ? "DESC" : "ASC";
    if (state.order !== newOrder) {
      switch (sortType) {
        case "NewPlayer":
          setSortPlayerState((prevState) => ({ ...prevState, order: newOrder }));
          setResultSortOrdering(newOrder);
          setIsPlayerFilterChange(true);
          onSortChange?.(state.filter, newOrder);
          break;
        case "Player":
          setSortState((prevState) => ({ ...prevState, order: newOrder }));
          setSortOrdering(newOrder);
          setIsFilterChange(true);
          onSortChange?.(state.filter, newOrder);
          break;
        case "Squad":
          setSortPositionState((prevState) => ({ ...prevState, order: newOrder }));
          setSortSquadOrdering(newOrder);
          setIsSquadFilterChange(true);
          onSortChange?.(state.filter, newOrder);
          break;
        case "Club":
          setSortClubState((prevState) => ({ ...prevState, order: newOrder }));
          setSortClubOrdering(newOrder);
          setIsClubSortFilterChange(true);
          setIsClubFilterChange(true);
          onSortChange?.(state.filter, newOrder);
          break;
        case "clubCandidate":
          setSortClubCandidateState((prevState) => ({ ...prevState, order: newOrder }));
          setSortClubCandidateOrdering(newOrder);
          setIsClubCandidateSortFilterChange(true);
          onSortChange?.(state.filter, newOrder);
          break;
        default:
          break;
      }
    }
  }, [filterState, sortType]);

  const getIsSelected = useCallback(
    (filter: string) => {
      return filterState[sortType as keyof typeof filterState]?.filter === filter;
    },
    [filterState, sortType]
  );

  const getSortIcon = useCallback(
    (filter: string) => {
      const state = filterState[sortType as keyof typeof filterState];
      return state.filter === filter ? <SecondaryButton iconType="Tick" className="filter-sort-button" /> : null;
    },
    [filterState, sortType]
  );

  const getToggleSortIcon = useCallback(
    (filter: string) => {
      const state = filterState[sortType as keyof typeof filterState];
      if (state.filter === filter) {
        return state.order === "ASC" ? (
          <SecondaryButton iconType="ArrowUp" className="filter-sort-button" onClick={toggleSortOrder} />
        ) : (
          <SecondaryButton iconType="ArrowDown" className="filter-sort-button" onClick={toggleSortOrder} />
        );
      }
      return null;
    },
    [filterState, sortType]
  );

  const itemsToRender = useMemo(() => {
    return Array.isArray(data) ? data : [...data.sortByFilters, ...data.performanceFilters, ...data.attributesFilters];
  }, [data]);

  return (
    <StyledSortContainer className="custom-scrollbar">
      <h3>SORT BY</h3>
      {itemsToRender.map((filter: any) => (
        <React.Fragment key={filter}>
          <StyledItemContainer isSelected={getIsSelected(filter)} onClick={() => handleSortChange(filter)}>
            {getSortIcon(filter)}
            <p>{filter}</p>
            <SortIconContainer>{getToggleSortIcon(filter)}</SortIconContainer>
          </StyledItemContainer>
          {filter === "Contract left" && (
            <>
              <StyledHR />
              <h3>PERFORMANCE SCORES</h3>
            </>
          )}
          {(sortType === "Player" || sortType === "NewPlayer" ) && filter === "System fit" && (
            <>
              <h3>ATTRIBUTES</h3>
            </>
          )}
        </React.Fragment>
      ))}
    </StyledSortContainer>
  );
};

export default FilterSort;
